import {
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@lib/utils";
import { LangContext, LangContextType } from "@utilities/customHooks";
import textData from "../../textData.json";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/ui/select";

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableSorting?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  stickyFirstColumn?: boolean;
  /** Number of leading columns to pin when scrolling horizontally.
   *  Overrides stickyFirstColumn when set. */
  stickyColumns?: number;
  stickyHeader?: boolean;
  /** Dynamic max-height for the inner scroll container (overrides default calc).
   *  Use with StickyPageHeader: pass `calc(100vh - var(--sticky-header-bottom, 280px))` */
  stickyMaxHeight?: string;
  /** Table flows in the outer page scroll instead of creating an inner scroll context.
   *  Use with StickyPageHeader for single-scroll UX. Horizontal scroll preserved. */
  unifiedScroll?: boolean;
  wideMode?: boolean;
  className?: string;
  getRowId?: (row: TData) => string;
  expandedRowIds?: Set<string>;
  renderExpandedRow?: (row: TData) => React.ReactNode | null;
  emptyMessage?: string;
}

export function DataTable<TData>({
  columns,
  data,
  onRowClick,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  enableSorting = false,
  enablePagination = false,
  pageSize = 25,
  stickyFirstColumn = false,
  stickyColumns: stickyColumnsProp,
  stickyHeader = false,
  stickyMaxHeight,
  unifiedScroll = false,
  wideMode = false,
  className,
  getRowId,
  expandedRowIds,
  renderExpandedRow,
  emptyMessage,
}: DataTableProps<TData>) {
  // Localized default for the empty state — callers can still override via prop.
  // LangContext's default value is null, so a provider-less mount (tests,
  // isolated renders) must not throw — fall back to English.
  const langCtx = useContext(LangContext) as LangContextType | null;
  const langState = langCtx?.langState ?? "eng";
  const chrome = textData.Components.DataTable;
  const emptyStateMessage = emptyMessage ?? chrome.NoResults[langState];
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Build state object
  const state: Record<string, unknown> = {};
  if (enablePagination) state.pagination = pagination;
  if (enableSorting) state.sorting = sorting;
  if (enableRowSelection && rowSelection !== undefined) state.rowSelection = rowSelection;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel(), onPaginationChange: setPagination }),
    ...(enableSorting && { getSortedRowModel: getSortedRowModel(), onSortingChange: setSorting, enableSorting: true }),
    ...(!enableSorting && { manualSorting: true }),
    ...(enableRowSelection && { enableRowSelection: true, onRowSelectionChange }),
    ...(Object.keys(state).length > 0 && { state }),
  });

  // Resolve sticky column count: new prop takes priority, fallback to boolean
  const stickyColCount = stickyColumnsProp ?? (stickyFirstColumn ? 1 : 0);

  // Scroll shadow for sticky columns
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrolledX, setScrolledX] = useState(false);

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      setScrolledX(containerRef.current.scrollLeft > 0);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !stickyColCount) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [stickyColCount, handleScroll]);

  // Publish sticky column left offsets as CSS vars (no React state, no re-renders)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!stickyColCount || !el) return;
    const headerCells = el.querySelectorAll("thead tr:first-child th");
    let cumLeft = 0;
    for (let i = 0; i < Math.min(stickyColCount, headerCells.length); i++) {
      el.style.setProperty(`--sticky-left-${i}`, `${cumLeft}px`);
      cumLeft += (headerCells[i] as HTMLElement).offsetWidth;
    }
    return () => {
      for (let i = 0; i < stickyColCount; i++) {
        el.style.removeProperty(`--sticky-left-${i}`);
      }
    };
  }, [stickyColCount, columns]);

  return (
    <div className={cn("w-full", className)}>
      <div
        ref={containerRef}
        className={cn(
          "listComp font-medium bg-backgrounds text-text mb-5 overflow-x-auto",
          stickyHeader && !unifiedScroll && "overflow-y-auto [&>div]:!overflow-visible",
          scrolledX && "scrolled-x"
        )}
        style={stickyHeader && !unifiedScroll ? { maxHeight: stickyMaxHeight || "calc(100vh - 280px)" } : undefined}
      >
        <Table>
          <TableHeader
            className={cn(
              stickyColCount > 0 && "stickyHeader",
              stickyHeader && !unifiedScroll && "sticky top-0 z-20 bg-white [&_tr]:bg-white shadow-sm"
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="border-b-0 hover:bg-transparent"
              >
                {headerGroup.headers.map((header, idx) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "text-cSecondaryGreen py-4 px-4 whitespace-nowrap",
                      wideMode && "min-w-[140px]",
                      idx < stickyColCount && "sticky z-30 bg-white",
                      idx === stickyColCount - 1 && "stickyFirstCol",
                      header.column.getCanSort() && "cursor-pointer select-none"
                    )}
                    style={{
                      ...(header.column.columnDef.minSize ? { minWidth: `${header.column.columnDef.minSize}px` } : undefined),
                      ...(idx < stickyColCount ? { left: `var(--sticky-left-${idx}, 0px)` } : undefined),
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <span className="text-xs">
                            {{ asc: "\u25B2", desc: "\u25BC" }[
                              header.column.getIsSorted() as string
                            ] ?? "\u25B4\u25BE"}
                          </span>
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, rowIdx) => {
                const rowId = getRowId?.(row.original) ?? row.id;
                const isExpanded = expandedRowIds?.has(rowId) && renderExpandedRow;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      className={cn(
                        "border-b border-borders transition-colors duration-200",
                        onRowClick && "cursor-pointer hover:bg-[#EFF5F4] [&>td]:hover:bg-[#EFF5F4]",
                        rowIdx % 2 === 1 && "bg-[#F7F7F7]",
                        rowIdx % 2 === 1 && "[&>td]:bg-[#F7F7F7]"
                      )}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell, cellIdx) => (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            "p-4 font-normal text-text/80 break-normal text-left",
                            wideMode && "min-w-[140px]",
                            cellIdx < stickyColCount && "sticky z-10 bg-white",
                            cellIdx === stickyColCount - 1 && "stickyFirstCol",
                            cellIdx < stickyColCount && rowIdx % 2 === 1 && "!bg-[#F7F7F7]"
                          )}
                          style={{
                            ...(cell.column.columnDef.minSize ? { minWidth: `${cell.column.columnDef.minSize}px` } : undefined),
                            ...(cellIdx < stickyColCount ? { left: `var(--sticky-left-${cellIdx}, 0px)` } : undefined),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && renderExpandedRow && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={row.getVisibleCells().length} className="p-0">
                          {renderExpandedRow(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              }))
            : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyStateMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {enablePagination && (
        <div className="flex items-center justify-between px-2 py-2">
          <div className="flex items-center gap-2 text-sm text-text/60">
            <span>{chrome.RowsPerPage[langState]}</span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(value) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: Number(value),
                  pageIndex: 0,
                }))
              }
            >
              <SelectTrigger className="border border-borders rounded px-2 py-1 bg-white h-auto w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-text/60">
              {chrome.PageWord[langState]} {pagination.pageIndex + 1} {chrome.Of[langState]}{" "}
              {table.getPageCount() || 1}
            </span>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 border border-borders rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EFF5F4]"
            >
              {chrome.Previous[langState]}
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 border border-borders rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EFF5F4]"
            >
              {chrome.Next[langState]}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
